<template>
	<div>
		<!-- date -->
		<v-card flat class="px-0">
			<v-card-text class="d-flex justify-space-between align-center pt-2">
				<v-dialog
					ref="monthDialog"
					v-model="monthModal"
					:return-value.sync="selectedMonth"
					persistent
					max-width="300px"
				>
					<template v-slot:activator="{ on, attrs }">
						<div v-bind="attrs" v-on="on" class="d-flex justify-space-between align-center primary--text">
							<div class="d-flex align-center">
								<v-icon class="me-2" color="primary">mdi-calendar</v-icon>
								<span class="text-h5 me-2">{{ dateHeaderLabel }}</span>
								<span class="text-subtitle-2 align-self-end">{{ moment(date, 'YYYY-MM-DD').format('YYYY') }}</span>
							</div>
						</div>
					</template>
					<v-date-picker
						v-model="selectedMonth"
						full-width
						picker-date
						type="month"
						:max="new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10)"
					>
						<v-spacer/>
						<v-btn text color="primary" @click="monthModal = false">{{ $T('cancel') }}</v-btn>
						<v-btn
							text
							color="primary"
							@click="
								$refs.monthDialog.save(selectedMonth);
								date = moment(selectedMonth, 'YYYY-MM').format('YYYY-MM-DD');
								fetchStock();
							"
						>
							{{ $T('ok') }}
						</v-btn>
					</v-date-picker>
				</v-dialog>

				<v-btn text small color="primary" @click="goToTodayDate">
					{{ $T('components.today') }}
				</v-btn>
			</v-card-text>
		</v-card>

		<v-day-date
			:value="date"
			:month="selectedMonth"
			:optional="false"
			@change="onDayChange"
		/>

		<div class="pt-3">
			<template v-if="!loading">
				<v-simple-table v-if="isToday || expenseEntries.length" dense class="expenses-table">
					<template v-slot:default>
						<thead class="primary white--text">
							<tr>
								<th class="white--text">{{ $T('Expenses.expense-name') }}</th>
								<th class="white--text">{{ $T('inputs.amount') }} ({{ $T('currency') }})</th>
								<th v-if="isToday"></th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="entry in expenseEntries" :key="entry.id">
								<td>
									<v-text-field
										:value="entry.name"
										flat
										solo
										background-color="transparent"
										hide-details
										:disabled="!isToday || savingIds.includes(entry.id)"
										:placeholder="$T('Expenses.expense-name')"
										@change="val => onExistingFieldChange(entry, { name: val })"
									/>
								</td>
								<td>
									<v-text-field
										:value="entry.amount"
										type="number"
										min="0"
										dense
										flat
										solo
										background-color="transparent"
										hide-details
										hide-spin-buttons
										:disabled="!isToday || savingIds.includes(entry.id)"
										placeholder="-"
										@keydown="blockDecimalKey"
										@change="val => onExistingFieldChange(entry, { amount: parseAmountInput(val) })"
									/>
								</td>
								<td v-if="isToday" class="text-center">
									<v-btn icon small color="error" :disabled="savingIds.includes(entry.id)" @click="removeExistingRow(entry)">
										<v-icon size="18">mdi-close</v-icon>
									</v-btn>
								</td>
							</tr>

							<tr v-if="isToday" class="expenses-table__row--empty">
								<td>
									<v-text-field
										:value="newRow.name"
										flat
										solo
										background-color="transparent"
										hide-details
										:disabled="addingRow"
										:placeholder="$T('Expenses.expense-name')"
										@change="val => onNewRowChange({ name: val })"
									/>
								</td>
								<td>
									<v-text-field
										:value="newRow.amount"
										type="number"
										min="0"
										dense
										flat
										solo
										background-color="transparent"
										hide-details
										hide-spin-buttons
										:disabled="addingRow"
										placeholder="-"
										@keydown="blockDecimalKey"
										@change="val => onNewRowChange({ amount: parseAmountInput(val) })"
									/>
								</td>
								<td></td>
							</tr>
						</tbody>
					</template>
				</v-simple-table>

				<div v-else class="d-flex flex-column justify-center align-center py-8">
					<v-icon x-large class="mb-3">mdi-cash-remove</v-icon>
					<span class="grey--text text--darken-2">{{ $T('Expenses.no-expenses-to-show') }}</span>
				</div>
			</template>

			<loader v-if="loading"/>
		</div>

		<!-- summary footer -->
		<v-footer app fixed padless>
			<v-card flat width="100%" class="expenses-summary">
				<v-card-text class="d-flex align-center justify-space-between py-3">
					<div class="d-flex align-center">
						<v-avatar size="36" color="primary lighten-5" class="me-3">
							<v-icon color="primary" size="20">mdi-cash-multiple</v-icon>
						</v-avatar>
						<span class="text-body-2 grey--text text--darken-2">{{ $T('Expenses.total-expenses') }}</span>
					</div>
					<span class="text-h6 font-weight-bold primary--text">
						{{ $options.filters.formatNumber(totalExpenses) }}
						<span class="text-body-2 font-weight-medium">{{ $T('currency') }}</span>
					</span>
				</v-card-text>
			</v-card>
		</v-footer>
	</div>
</template>

<script>
import moment from 'moment'
import { mapState, mapGetters } from 'vuex'
import { parseFormattedNumber } from '@/helpers/functions'
import VDayDate from '@/components/VDayDate.vue'
import Loader from '@/components/Loader.vue'

export default {
	name: 'Expenses',

	components: {
		VDayDate,
		Loader
	},

	data() {
		return {
			moment,

			date: moment().format('YYYY-MM-DD'),
			selectedMonth: moment().format('YYYY-MM'),
			monthModal: false,
			loading: true,

			newRow: { name: null, amount: null },
			addingRow: false,
			savingIds: []
		}
	},

	computed: {
		dateHeaderLabel() {
			const dayLabel = this.isToday ? this.$T('components.today') : moment(this.date, 'YYYY-MM-DD').format('dddd')
			return `${dayLabel}, ${moment(this.date, 'YYYY-MM-DD').format('DD MMMM')}`
		},

		isToday() {
			return this.date === moment().format('YYYY-MM-DD')
		},

		totalExpenses() {
			return this.expenseEntries.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0)
		},

		...mapState({
			realNumberAmount: state => state.configurations.realNumberAmount
		}),

		...mapGetters({
			expenseEntries: 'employeeStock/getExpenseEntries'
		})
	},

	methods: {
		fetchStock() {
			this.loading = true
			return this.$store.dispatch('employeeStock/fetchByDate', { date: this.date })
				.finally(() => {
					this.loading = false
				})
		},

		goToTodayDate() {
			const today = moment().format('YYYY-MM-DD')
			if (this.date !== today) {
				this.selectedMonth = moment().format('YYYY-MM')
				this.date = today
				this.fetchStock()
			}
		},

		onDayChange(newDate) {
			if (newDate === this.date) return
			this.date = newDate
			this.fetchStock()
		},

		blockDecimalKey(event) {
			if (!this.realNumberAmount && (event.key === '.' || event.key === ',')) {
				event.preventDefault()
			}
		},

		parseAmountInput(val) {
			return val === '' || val === null ? null : parseFormattedNumber(val, { allowDecimal: this.realNumberAmount })
		},

		onExistingFieldChange(entry, patch) {
			if (this.savingIds.includes(entry.id)) return

			const name = patch.name !== undefined ? patch.name : entry.name
			const amount = patch.amount !== undefined ? patch.amount : entry.amount
			if (!name || !amount || amount <= 0) return

			this.savingIds.push(entry.id)
			this.$store.dispatch('employeeStock/updateExpenses', {
				entries: this.expenseEntries.map(e => e.id === entry.id ? { ...e, name, amount } : e)
			}).finally(() => {
				this.savingIds = this.savingIds.filter(id => id !== entry.id)
			})
		},

		removeExistingRow(entry) {
			this.savingIds.push(entry.id)
			this.$store.dispatch('employeeStock/delete', { id: entry.id }).finally(() => {
				this.savingIds = this.savingIds.filter(id => id !== entry.id)
			})
		},

		onNewRowChange(patch) {
			Object.assign(this.newRow, patch)
			if (this.addingRow || !this.newRow.name || !this.newRow.amount || this.newRow.amount <= 0) return

			this.addingRow = true
			const { name, amount } = this.newRow
			this.$store.dispatch('employeeStock/addExpense', { date: this.date, name, amount })
				.then(() => {
					this.newRow = { name: null, amount: null }
				})
				.finally(() => {
					this.addingRow = false
				})
		}
	},

	mounted() {
		this.fetchStock()
	},

	beforeDestroy() {
		this.$store.dispatch('employeeStock/reset')
	}
}
</script>

<style scoped>
/*
 * vuetify reaches these cells through a long structural chain
 * (.v-data-table > .v-data-table__wrapper > table > thead > tr > th), so the
 * overrides below repeat it to out-weigh it rather than reach for !important
 */
.expenses-table.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th {
	background-color: transparent;
	border-bottom: none;
	padding: 0 4px;
	white-space: nowrap;
}

.expenses-table.v-data-table > .v-data-table__wrapper > table > thead > tr > th:not(:last-child) {
	border-right: 1px solid rgba(255, 255, 255, 0.35);
}

.expenses-table.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
	border-right: 1px solid #e0e0e0;
	border-bottom: 1px solid #e0e0e0;
	padding: 0 4px;
}

.expenses-table.v-data-table > .v-data-table__wrapper > table > thead > tr > th:last-child,
.expenses-table.v-data-table > .v-data-table__wrapper > table > tbody > tr > td:last-child {
	width: 1%;
	padding: 0;
	border-right: none;
}

.expenses-table tbody td >>> .v-text-field > .v-input__control > .v-input__slot {
	padding: 0 2px;
}

.expenses-table__row--empty {
	background-color: #f5f5f5;
}

.expenses-summary {
	border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
